/**
 * Shopper payment methods
 * ─────────────────────────────────────────────
 * Mirrors the shape returned by the listPaymentMethods, deletePaymentMethod,
 * and setDefaultPaymentMethod callables in functions/src/services/payment-methods.ts.
 */

export interface SavedPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  funding: string;
  /** 'apple_pay' | 'google_pay' | 'link' when the card came from a wallet. */
  wallet: string | null;
  isDefault: boolean;
}

export interface PaymentMethodsResponse {
  paymentMethods: SavedPaymentMethod[];
  defaultPaymentMethodId: string | null;
}

const BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

const WALLET_LABELS: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  link: 'Link',
  samsung_pay: 'Samsung Pay',
};

export function formatCardBrand(brand: string): string {
  return BRAND_LABELS[brand?.toLowerCase()] || 'Card';
}

export function formatWallet(wallet: string | null): string | null {
  if (!wallet) return null;
  return WALLET_LABELS[wallet] || null;
}

/** "04/27", or an empty string when Stripe returned no expiry. */
export function formatExpiry(expMonth: number, expYear: number): string {
  if (!expMonth || !expYear) return '';
  return `${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`;
}

export function isCardExpired(expMonth: number, expYear: number): boolean {
  if (!expMonth || !expYear) return false;
  const now = new Date();
  // A card is valid through the last day of its expiry month.
  return expYear < now.getFullYear() ||
    (expYear === now.getFullYear() && expMonth < now.getMonth() + 1);
}
